<template>
    <div>
        <v-row>
            <v-col cols="12" md="6">
                <AccentTitleCard class="fill-height d-flex flex-column" :title-class="' py-2'">
                    <template #title>
                        <font-awesome-icon icon="fa-solid fa-images" class="mr-2" />
                        <span> Maximum Allowed Slides </span>
                    </template>

                    <template #body>
                        <v-card-text class="fill-height">
                            <span> General settings that apply to the tribute editor. </span>

                            <div class="fill-height d-flex flex-column justify-center">
                                <div class="d-flex justify-space-between align-center" v-if="mode === 'tribute'">
                                    <span class="overline mb-2">
                                        Submissions {{ settings.submissionsOpen ? 'Open' : 'Closed' }}
                                    </span>
                                    <v-switch
                                        v-model="settings.submissionsOpen"
                                        class="ma-0 pa-0"
                                        hide-details
                                        dense
                                        color="#ff530d"
                                    ></v-switch>
                                </div>

                                <v-expand-transition>
                                    <div v-show="mode !== 'tribute' || settings.submissionsOpen">
                                        <div class="d-flex" style="gap: 12px">
                                            <div class="flex-grow-1">
                                                <div class="d-flex justify-space-between text--secondary">
                                                    <span>Minimum ({{ allowedSlidesRangeMin }})</span>
                                                    <span>Maximum ({{ allowedSlidesRangeMax }})</span>
                                                </div>
                                                <v-slider
                                                    :disabled="mode === 'tribute' && !settings.submissionsOpen"
                                                    color="#ff530d"
                                                    track-color="#eeeeee"
                                                    v-model="settings.maxItems"
                                                    :min="allowedSlidesRangeMin"
                                                    :max="
                                                        maxItemsChanged
                                                            ? allowedSlidesRangeMax
                                                            : Math.max(settings.maxItems, allowedSlidesRangeMax)
                                                    "
                                                    @change="maxItemsChanged = true"
                                                ></v-slider>
                                            </div>

                                            <div class="d-flex align-center">
                                                <v-text-field
                                                    :disabled="mode === 'tribute' && !settings.submissionsOpen"
                                                    style="max-width: 80px"
                                                    dense
                                                    outlined
                                                    color="#ff530d"
                                                    v-model="settings.maxItems"
                                                    @change="maxItemsChanged = true"
                                                    type="number"
                                                >
                                                </v-text-field>
                                            </div>
                                        </div>
                                        <v-divider></v-divider>
                                        <div class="d-flex justify-space-between align-items-center mb-2">
                                            <span class="overline" :class="{ 'setting-inactive': !limitSessionItems }">
                                                Maximum Slides Per Contributor
                                            </span>
                                            <v-switch
                                                :disabled="mode === 'tribute' && !settings.submissionsOpen"
                                                v-model="limitSessionItems"
                                                class="ma-0 pa-0"
                                                hide-details
                                                dense
                                                color="#ff530d"
                                            ></v-switch>
                                        </div>

                                        <v-expand-transition>
                                            <div v-show="limitSessionItems">
                                                <p class="d-flex justify-space-between align-center">
                                                    <span>This setting applies to "upload only" contributors. </span>
                                                </p>
                                                <div class="d-flex" style="gap: 12px">
                                                    <div class="flex-grow-1">
                                                        <div class="d-flex justify-space-between text--secondary">
                                                            <span>Minimum ({{ allowedSlidesRangeMin }})</span>
                                                            <span>Maximum ({{ settings.maxItems }})</span>
                                                        </div>
                                                        <v-slider
                                                            color="#ff530d"
                                                            track-color="#eeeeee"
                                                            :disabled="mode === 'tribute' && !settings.submissionsOpen"
                                                            v-model="settings.maxSessionItems"
                                                            :min="allowedSlidesRangeMin"
                                                            :max="settings.maxItems"
                                                            @change="maxSessionItemsChanged = true"
                                                        ></v-slider>
                                                    </div>

                                                    <div class="d-flex align-center">
                                                        <v-text-field
                                                            style="max-width: 80px"
                                                            :disabled="mode === 'tribute' && !settings.submissionsOpen"
                                                            dense
                                                            outlined
                                                            color="#ff530d"
                                                            v-model="settings.maxSessionItems"
                                                            @change="maxSessionItemsChanged = true"
                                                            type="number"
                                                        >
                                                        </v-text-field>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expand-transition>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </v-card-text>
                    </template>
                </AccentTitleCard>
            </v-col>
            <v-col cols="12" md="6">
                <AccentTitleCard class="fill-height d-flex flex-column" :title-class="' py-2'">
                    <template #title>
                        <font-awesome-icon icon="fa-solid fa-film" class="mr-2" />
                        <span> Outro Video </span>
                    </template>

                    <template #body>
                        <v-card-text>
                            <p class="d-flex justify-space-between align-center">
                                <span> Upload a video to be appended to every tribute. </span>

                                <CustomTooltip :tooltipProps="{ top: true }" v-if="mode === 'tribute'">
                                    <template #activator>
                                        <font-awesome-icon
                                            class="pointer"
                                            @click="openGlobalSettingsTab"
                                            icon="fa-regular fa-arrow-up-right-from-square"
                                        ></font-awesome-icon>
                                    </template>

                                    <template #content>
                                        <span>Default Settings</span>
                                    </template>
                                </CustomTooltip>
                            </p>

                            <div class="upload-wrapper">
                                <v-btn
                                    v-if="settings.tributeEndingVideo && mode == 'global'"
                                    @click="deleteEndingVideoConfirmModal = true"
                                    :disabled="loading"
                                    x-small
                                    fab
                                    color="error"
                                    class="outro-delete-btn"
                                >
                                    <font-awesome-icon icon="fa-solid fa-trash" />
                                </v-btn>
                                <video
                                    v-if="settings.tributeEndingVideo"
                                    controls
                                    :key="settings.tributeEndingVideo"
                                    class="ending-video elevation-2"
                                    :src="settings.tributeEndingVideo"
                                ></video>

                                <div
                                    class="upload-placeholder text-center"
                                    @click.stop="handleEndingVideoUpdateClick"
                                    v-else
                                >
                                    <font-awesome-icon
                                        class="outro-upload-icon mb-5"
                                        icon="fa-solid fa-cloud-arrow-up"
                                    />

                                    <span v-if="mode === 'global'">Click to upload an outro video</span>
                                    <span v-if="mode === 'global'" class="caption mt-2">MP4 format recommended</span>
                                    <span v-if="mode === 'tribute'"
                                        >Click to navigate to global outro video settings</span
                                    >
                                </div>
                            </div>
                        </v-card-text>
                    </template>
                </AccentTitleCard>

                <MediaUploader
                    v-if="funeralHomeId && token && mode === 'global'"
                    ref="videoUpload"
                    :token="token"
                    :uploaderType="MEDIA_UPLOADER_TYPES.TRIBUTE_VIDEO_ENDING_VIDEO"
                    :itemId="funeralHomeId"
                    :allowedFileTypes="['.mp4', '.mov']"
                    @refresh="getSettings"
                />
            </v-col>
            <v-col :key="refreshKey" cols="12">
                <AccentTitleCard :title-class="' py-2'">
                    <template #title>
                        <font-awesome-icon icon="fa-solid fa-list-check" class="mr-2" />
                        <span> Desired Workflow </span>
                    </template>

                    <template #body>
                        <v-card-text>
                            <p>Choose how you want to distribute the work of creating the tribute video.</p>

                            <v-row>
                                <v-col cols="12" md="4">
                                    <div
                                        @click="selectWorkflowOption(2)"
                                        :class="{ selected: settings.tributeWorkflow === 2 }"
                                        class="workflow-option"
                                    >
                                        <div class="selected-indicator">
                                            <div v-if="settings.tributeWorkflow === 2" class="selected-circle">
                                                <div class="selected-circle-inner"></div>
                                            </div>

                                            <div v-else class="deselected-circle"></div>
                                        </div>
                                        <div class="icon-container">
                                            <font-awesome-icon class="workflow-icon" icon="fa-solid fa-users" />
                                        </div>
                                        <div>
                                            <h6>Family Does everything</h6>
                                            <p>
                                                Family uploads slides, chooses music and theme, and can also create the
                                                video
                                            </p>
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div
                                        @click="selectWorkflowOption(0)"
                                        :class="{ selected: settings.tributeWorkflow === 0 }"
                                        class="workflow-option"
                                    >
                                        <div class="selected-indicator">
                                            <div v-if="settings.tributeWorkflow === 0" class="selected-circle">
                                                <div class="selected-circle-inner"></div>
                                            </div>

                                            <div v-else class="deselected-circle"></div>
                                        </div>
                                        <div class="icon-container">
                                            <font-awesome-icon class="workflow-icon" icon="fa-solid fa-handshake" />
                                        </div>
                                        <div>
                                            <h6>Hybrid</h6>
                                            <p>
                                                Family can choose theme and upload music, funeral home still puts
                                                together the video
                                            </p>
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div
                                        @click="selectWorkflowOption(1)"
                                        :class="{ selected: settings.tributeWorkflow === 1 }"
                                        class="workflow-option"
                                    >
                                        <div class="selected-indicator">
                                            <div v-if="settings.tributeWorkflow === 1" class="selected-circle">
                                                <div class="selected-circle-inner"></div>
                                            </div>
                                            <div v-else class="deselected-circle"></div>
                                        </div>
                                        <div class="icon-container">
                                            <font-awesome-icon class="workflow-icon" icon="fa-solid fa-building" />
                                        </div>
                                        <div>
                                            <h6>Funeral Home Does the Work</h6>
                                            <p>Family only uploads, funeral home does everything else</p>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </template>
                </AccentTitleCard>
            </v-col>
        </v-row>

        <v-dialog persistent v-model="deleteEndingVideoConfirmModal" max-width="400">
            <v-card>
                <v-card-title> Delete Outro Video? </v-card-title>
                <v-card-text> Click confirm to continue. </v-card-text>

                <v-card-actions class="d-flex justify-end">
                    <v-btn class="no-text-transform" outlined @click="deleteEndingVideoConfirmModal = false" depressed
                        >Cancel</v-btn
                    >
                    <v-btn class="no-text-transform" @click="deleteEndingVideo" depressed color="error">Confirm</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import MediaUploader from '@/components/ui/MediaUploader.vue';
import AccentTitleCard from '@/components/ui/AccentTitleCard.vue';
import TributeVideoService from '@/services/tributeVideo.service';
import { mapActions } from 'pinia';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import { MEDIA_UPLOADER_TYPES } from '@/constants';
import FuneralHomeService from '@/services/funeralHome.service';
import { useTributeVideoStore } from '@/stores/tributeVideo';
import { useGlobalState } from '@/stores/globalState';

export default {
    setup() {
        return {
            tributeVideoStore: useTributeVideoStore(),
        }
    },
    data() {
        return {
            MEDIA_UPLOADER_TYPES,
            settings: {
                maxItems: 200,
                maxSessionItems: null,
                tributeWorkflow: null,
                tributeEndingVideo: null,
            },
            limitSessionItems: false,
            tmpSettings: null,
            tmpSessionLimit: null,
            loading: true,
            allowedSlidesRangeMin: 10,
            allowedSlidesRangeMax: 400,
            defaultMaxPerSession: 50,
            refreshKey: 0,
            deleteEndingVideoConfirmModal: false,
            maxItemsChanged: false,
            maxSessionItemsChanged: false,
        };
    },

    components: { CustomTooltip, AccentTitleCard, MediaUploader },
    props: {
        tributeVideoId: {
            type: Number,
            required: false,
        },
        funeralHomeId: {
            type: Number,
            required: true,
        },
        mode: {
            type: String,
            required: true,
            validator: value => ['tribute', 'global'].includes(value),
        },
        token: {
            type: String,
            required: true,
        },
    },

    computed: {
        tributeDefaultSettingsLink() {
            if (this.$auth.role.includes('SuperAdmin') && this.funeralHomeId > 0) {
                return `${process.env.VUE_APP_BASE_URI}/homes/settings/${this.funeralHomeId}?tab=tribute`;
            }

            if (this.$auth.role.includes('FuneralHome') || this.$auth.role.includes('Owner')) {
                const fhId = this.$auth?.user['https://memoryshare.com/funeral_home_id'];

                if (fhId > 0) {
                    return `${process.env.VUE_APP_BASE_URI}/settings?tab=tribute`;
                }
            }

            return null;
        },
        hasNoChangesChanges() {
            return (
                JSON.stringify(this.settings) === JSON.stringify(this.tmpSettings) &&
                this.tmpSessionLimit === this.limitSessionItems
            );
        },
    },
    watch: {
        'settings.tributeWorkflow'(newVal) {
            this.refreshKey++;
        },

        loading(newVal) {
            this.$emit('loading', newVal);
        },
        hasNoChangesChanges(newVal) {
            this.$emit('unsaved-changes', !newVal);
        },
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar']),
        async deleteEndingVideo() {
            try {
                this.deleteEndingVideoConfirmModal = false;
                let id = this.funeralHomeId;

                if (!id) {
                    throw new Error('Invalid funeralHomeId');
                }

                this.loading = true;
                await this.fhApiService.deleteTributeEndingVideo(id);
                this.settings.tributeEndingVideo = '';
                this.refreshKey++;

                this.showSnackbar({ message: 'Settings successfuly updated' });
            } catch (error) {
                console.log(error, 'error deleting ending video');
                this.showSnackbar({ message: 'Error! Settings not updated' });
            } finally {
                this.loading = false;
            }
        },
        handleEndingVideoUpdateClick() {
            if (this.loading) {
                this.showSnackbar({ message: 'Please wait for settings to finish loading' });
                return;
            }

            if (this.mode === 'tribute') {
                this.openGlobalSettingsTab();
            }

            if (this.mode === 'global') {
                this.$refs.videoUpload.handleOpen();
            }
        },
        openGlobalSettingsTab() {
            window.open(this.tributeDefaultSettingsLink, '_blank');
        },
        selectWorkflowOption(val) {
            this.settings.tributeWorkflow = val;
        },
        initApiService() {
            if (this.token && !this.tributeApiService) {
                this.tributeApiService = TributeVideoService(this.token);
            }
        },
        async getSettings() {
            try {
                this.loading = true;

                let resp;

                if (this.mode === 'tribute') {
                    resp = await this.tributeApiService.getSettings(this.tributeVideoId);
                } else if (this.mode === 'global') {
                    resp = await this.fhApiService.getTributeSettings(this.funeralHomeId);
                }

                const { data } = resp;

                if (data) {
                    console.log(data, 'settings data');
                    this.settings = data;

                    this.limitSessionItems = this.settings?.maxSessionItems > 0;

                    if (!this.settings?.maxSessionItems) {
                        this.settings.maxSessionItems = 100;
                    }

                    this.tmpSettings = JSON.parse(JSON.stringify(this.settings));
                    this.tmpSessionLimit = this.limitSessionItems;

                    this.maxItemsChanged = false;
                    this.maxSessionItemsChanged = false;

                    this.tributeVideoStore.updateTributeVideo({
                        maxItems: data.maxItems,
                        maxSessionItems: data.maxSessionItems,
                        tributeWorkflow: data.tributeWorkflow,
                        submissionsOpen: data.submissionsOpen,
                        tributeEndingVideo: data.tributeEndingVideo,
                    });
                }
            } catch (error) {
                console.log(error);
            } finally {
                this.loading = false;
            }
        },
        async saveSettings() {
            try {
                this.loading = true;
                // Note: this will only work so long as the property names match the server
                const savePayload = { ...this.settings };

                if (!this.maxItemsChanged) {
                    savePayload.maxItems = this.tmpSettings.maxItems;
                }

                if (!this.maxSessionItemsChanged) {
                    savePayload.maxSessionItems = this.tmpSettings.maxSessionItems;
                }

                if (!this.limitSessionItems) savePayload.maxSessionItems = null;

                let resp;

                if (this.mode === 'tribute') {
                    resp = await this.tributeApiService.updateSettings(this.tributeVideoId, savePayload);
                } else if (this.mode === 'global') {
                    resp = await this.fhApiService.updateTributeSettings(this.funeralHomeId, savePayload);
                }

                const { data, status } = resp;

                if (status === 200) {
                    this.tributeVideoStore.updateTributeVideo({
                        maxItems: data.maxItems,
                        maxSessionItems: data.maxSessionItems,
                        tributeWorkflow: data.tributeWorkflow,
                        submissionsOpen: data.submissionsOpen,
                        tributeEndingVideo: data.tributeEndingVideo,
                    });
                    this.showSnackbar({ message: 'Settings saved.' });
                    this.$emit('update-success');
                }
            } catch (error) {
                console.log('error saving settings', error);
                this.showSnackbar({ message: 'Error updating settings', color: 'error' });
            } finally {
                this.loading = false;
            }
        },
    },
    async mounted() {
        this.tributeApiService = TributeVideoService(this.token);
        this.fhApiService = FuneralHomeService(this.token);
        this.getSettings();
    },
};
</script>
<style lang="scss" scoped>
.workflow-option {
    padding: 16px;
    border: 2px solid #eeeeee;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    cursor: pointer;
    height: 100%;
}

.workflow-option.selected {
    border-color: #ff7837;
    background-color: lighten(#ff7837, 37%);
}

.workflow-icon {
    color: #ff530d;
    font-size: 1.8rem;
}

.icon-container {
    background-color: lighten(#ff7837, 32%);
    width: 50px;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
}

.selected-indicator {
    position: absolute;
    top: 16px;
    right: 16px;

    .selected-circle {
        border: 2px solid #ff7837;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        justify-content: center;
        align-items: center;

        .selected-circle-inner {
            background-color: #ff530d;
            border-radius: 50%;
            width: 12px;
            height: 12px;
        }
    }

    .deselected-circle {
        border: 2px solid $medium-gray;
        border-radius: 50%;
        width: 20px;
        height: 20px;
    }
}

.upload-wrapper {
    width: 100%;
    aspect-ratio: 16/9;
    position: relative;
    padding: 0;
    margin: 0;

    .ending-video {
        width: 100%;
        min-height: 100%;
        max-height: 100%;
        aspect-ratio: 16/9;
        max-width: 100%;
        object-fit: cover;
        border-radius: 5px;
        margin: 0;
        padding: 0;
        background-color: black;
    }

    .upload-placeholder {
        border: 2px dashed #eeeeee;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        cursor: pointer;
    }

    .upload-placeholder:hover {
        background-color: $light-gray;
    }

    .outro-upload-icon {
        font-size: 3rem;
        color: $medium-gray;
    }

    .outro-delete-btn {
        position: absolute;
        top: 10px;
        right: 10px;

        z-index: 5;
    }
}

.setting-inactive {
    opacity: 0.5;
}
</style>
